'use client';

import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from '../home/components/ParticleCanvas';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function HelpPage() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <>
      {/* Fixed canvas particle background */}
      <ParticleCanvas />
      {/* Vignette overlay */}
      <div className="vignette" aria-hidden="true" />
      {/* Navigation */}
      <Header />
      {/* Main content */}
      <main className="relative min-h-screen pt-32 pb-20 px-6" style={{ zIndex: 2 }}>
        <div 
          ref={sectionRef}
          className={`max-w-5xl mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Large bold headline */}
          <h1 
            className="text-6xl md:text-7xl font-black text-center mb-4"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #c4beff 50%, #6a5cff 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Help & Support
          </h1>

          {/* Subtitle */}
          <p className="text-center text-vanta-muted text-lg mb-16 max-w-2xl mx-auto">
            Support is handled via Discord tickets with fast response times from our dedicated team.
          </p>

          {/* Main support card with glassmorphism */}
          <div 
            className="relative rounded-3xl p-12 md:p-16 flex flex-col items-center text-center overflow-hidden group"
            style={{
              background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.08) 0%, rgba(139, 127, 255, 0.05) 100%)',
              border: '2px solid rgba(106, 92, 255, 0.2)',
              backdropFilter: 'blur(30px)',
              boxShadow: '0 25px 80px rgba(106, 92, 255, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            }}
          >
            {/* Subtle background glow effect */}
            <div 
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
              style={{
                background: 'radial-gradient(circle at 50% 50%, rgba(106, 92, 255, 0.15) 0%, transparent 70%)',
              }}
            />

            {/* Discord icon at top */}
            <div 
              className="relative w-20 h-20 rounded-2xl flex items-center justify-center mb-8 transition-all duration-500 group-hover:scale-110"
              style={{
                background: 'linear-gradient(135deg, rgba(106, 92, 255, 0.25) 0%, rgba(139, 127, 255, 0.15) 100%)',
                border: '2px solid rgba(106, 92, 255, 0.4)',
                boxShadow: '0 10px 40px rgba(106, 92, 255, 0.3)',
              }}
            >
              <svg 
                className="w-10 h-10" 
                viewBox="0 0 24 24" 
                fill="#6a5cff"
              >
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
            </div>

            {/* Primary CTA button */}
            <a
              href="https://discord.gg/gJgHMDZAES"
              target="_blank"
              rel="noopener noreferrer"
              className="relative mb-12 px-10 py-5 text-xl font-bold text-white rounded-2xl overflow-hidden group/btn transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #6a5cff 0%, #8b7fff 100%)',
                boxShadow: '0 10px 40px rgba(106, 92, 255, 0.4), 0 0 0 0 rgba(106, 92, 255, 0.5)',
                animation: 'pulse-glow 2s ease-in-out infinite',
              }}
              onMouseDown={(e) => {
                const ripple = document.createElement('span');
                const rect = e?.currentTarget?.getBoundingClientRect();
                const size = Math.max(rect?.width, rect?.height);
                const x = e?.clientX - rect?.left - size / 2;
                const y = e?.clientY - rect?.top - size / 2;
                ripple.style.width = ripple.style.height = `${size}px`;
                ripple.style.left = `${x}px`;
                ripple.style.top = `${y}px`;
                ripple.className = 'ripple-effect';
                e?.currentTarget?.appendChild(ripple);
                setTimeout(() => ripple?.remove(), 600);
              }}
            >
              <span className="relative z-10 flex items-center gap-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Open Support Ticket
              </span>
              <div 
                className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'linear-gradient(135deg, #8b7fff 0%, #6a5cff 100%)',
                }}
              />
            </a>

            {/* 4 feature points with icons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-3xl mb-12">
              {/* Feature 1 */}
              <div className="flex items-start gap-4 text-left">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(106, 92, 255, 0.15)',
                    border: '1px solid rgba(106, 92, 255, 0.3)',
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">Fast response from active staff</h3>
                  <p className="text-vanta-muted text-sm">Our team is online and ready to help you quickly</p>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="flex items-start gap-4 text-left">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(106, 92, 255, 0.15)',
                    border: '1px solid rgba(106, 92, 255, 0.3)',
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">Ban appeals handled professionally</h3>
                  <p className="text-vanta-muted text-sm">Fair review process for all ban appeal requests</p>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="flex items-start gap-4 text-left">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(106, 92, 255, 0.15)',
                    border: '1px solid rgba(106, 92, 255, 0.3)',
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">Player reports reviewed seriously</h3>
                  <p className="text-vanta-muted text-sm">Every report is investigated thoroughly by our team</p>
                </div>
              </div>

              {/* Feature 4 */}
              <div className="flex items-start gap-4 text-left">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'rgba(106, 92, 255, 0.15)',
                    border: '1px solid rgba(106, 92, 255, 0.3)',
                  }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="#6a5cff" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-1">Secure and verified ticket system</h3>
                  <p className="text-vanta-muted text-sm">Private, encrypted tickets for your safety and privacy</p>
                </div>
              </div>
            </div>

            {/* Live status indicator */}
            <div className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full animate-pulse"
                style={{ 
                  background: '#10b981',
                  boxShadow: '0 0 12px rgba(16, 185, 129, 0.8), 0 0 24px rgba(16, 185, 129, 0.4)',
                }}
              />
              <span className="text-sm font-bold uppercase tracking-wider" style={{ color: '#10b981' }}>
                Support Staff Online
              </span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <style jsx>{`
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 10px 40px rgba(106, 92, 255, 0.4), 0 0 0 0 rgba(106, 92, 255, 0.5);
          }
          50% {
            box-shadow: 0 10px 60px rgba(106, 92, 255, 0.6), 0 0 0 8px rgba(106, 92, 255, 0);
          }
        }
      `}</style>
    </>
  );
}